// src/pages/Reports/IssuanceReporting/IssuanceStock/IssuanceRangeTab.tsx
// RPT-002 (README.md mục 7) — chi tiết phát hành theo dải. BẮT BUỘC chọn 1 kỳ phát hành trước
// khi tải (BE giới hạn kích thước dữ liệu, không cho quét toàn hệ thống 1 lúc — xem
// GetTicketIssuanceRangeReportQuery bên ticket-service).

import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Col, Input, Label, Row, Spinner } from 'reactstrap';
import { toast } from 'react-toastify';

import DataTableShell, { getSttNumber } from 'Components/Common/DataTableShell';
import AgentPagination from 'pages/AgentManagement/AgentPagination';
import { getIssuanceRangeReportApi } from 'helpers/fakebackend_helper';
import { ISSUANCE_REPORTS_BASE } from 'helpers/url_helper';
import IssuancePlanSelect from '../shared/IssuancePlanSelect';
import { cleanParams, formatDateTime, formatQty, formatCurrency, getPaginatedPayload } from '../shared/formatters';
import { downloadReportExport } from '../shared/exportReport';
import { INITIAL_REPORT_PAGINATION, ReportPaginationState, TicketIssuanceRangeReportRow } from '../shared/types';
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('reports_issuance_reporting');

const STATUS_OPTIONS = [
  { value: 'DaGiao', label: st('da_giao_cho_dai_ly_label') },
  { value: 'DaThuHoi', label: st('da_thu_hoi_label') },
  { value: 'ConKho', label: st('con_trong_kho_label') },
];

const STATUS_BADGE: Record<string, string> = {
  DaGiao: 'primary',
  DaThuHoi: 'warning',
  ConKho: 'success',
};

const IssuanceRangeTab: React.FC = () => {
  const [planId, setPlanId] = useState('');
  const [symbol, setSymbol] = useState('');
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState<'Xlsx' | 'Csv' | null>(null);
  const [rows, setRows] = useState<TicketIssuanceRangeReportRow[]>([]);
  const [pagination, setPagination] = useState<ReportPaginationState>(INITIAL_REPORT_PAGINATION);

  const buildFilterParams = useCallback(
    () => ({
      IssuancePlanId: planId || undefined,
      Symbol: symbol || undefined,
      Status: status || undefined,
    }),
    [planId, symbol, status],
  );

  const loadReport = useCallback(async () => {
    if (!planId) {
      setRows([]);
      setPagination(INITIAL_REPORT_PAGINATION);
      return;
    }
    setLoading(true);
    try {
      const res = await getIssuanceRangeReportApi(
        cleanParams({
          ...buildFilterParams(),
          PageIndex: pagination.currentPage,
          PageSize: pagination.pageSize,
        }),
      );
      const { items, totalItems, totalPages } = getPaginatedPayload(res);
      setRows(items);
      setPagination((prev) => ({ ...prev, totalItems, totalPages }));
    } catch (error: any) {
      toast.error(error?.message || st('khong_the_tai_bao_cao_chi_message_3'));
    } finally {
      setLoading(false);
    }
  }, [buildFilterParams, pagination.currentPage, pagination.pageSize, planId]);

  useEffect(() => {
    loadReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.currentPage, pagination.pageSize, planId, symbol, status]);

  const handleFilterChange = (setter: (value: string) => void) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setter(event.target.value);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handleExport = async (format: 'Xlsx' | 'Csv') => {
    if (!planId) return;
    setExporting(format);
    try {
      await downloadReportExport(`${ISSUANCE_REPORTS_BASE}/IssuanceRange`, buildFilterParams(), format, 'ChiTietPhatHanhTheoDai');
    } catch (error: any) {
      toast.error(error?.message || st('khong_the_xuat_bao_cao_message'));
    } finally {
      setExporting(null);
    }
  };

  return (
    <>
      <Row className="g-3 mb-3 align-items-end">
        <Col md={3}>
          <Label className="form-label">{st('ky_phat_hanh_label')} <span className="text-danger">*</span></Label>
          <IssuancePlanSelect requireSelection value={planId} onChange={(v) => { setPlanId(v); setPagination((p) => ({ ...p, currentPage: 1 })); }} />
        </Col>
        <Col md={3}>
          <Label className="form-label">{st('ky_hieu_label')}</Label>
          <Input type="text" placeholder={st('vd_n_b_placeholder')} value={symbol} onChange={handleFilterChange(setSymbol)} disabled={!planId} />
        </Col>
        <Col md={3}>
          <Label className="form-label">{st('trang_thai_column')}</Label>
          <Input type="select" value={status} onChange={handleFilterChange(setStatus)} disabled={!planId}>
            <option value="">{st('tat_ca_trang_thai_option')}</option>
            {STATUS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </Input>
        </Col>
        <Col md={3} className="d-flex gap-2 justify-content-end">
          <Button color="success" outline size="sm" disabled={!planId || !!exporting} onClick={() => handleExport('Xlsx')}>
            {exporting === 'Xlsx' ? <Spinner size="sm" /> : <i className="ri-file-excel-2-line align-bottom me-1"></i>}
            {st('excel_button')}
          </Button>
          <Button color="secondary" outline size="sm" disabled={!planId || !!exporting} onClick={() => handleExport('Csv')}>
            {exporting === 'Csv' ? <Spinner size="sm" /> : <i className="ri-file-text-line align-bottom me-1"></i>}
            {st('csv_button')}
          </Button>
        </Col>
      </Row>

      {!planId ? (
        <Alert color="info" className="mb-0">{st('vui_long_chon_ky_phat_hanh_message')}</Alert>
      ) : loading ? (
        <div className="text-center py-5"><Spinner color="primary" /></div>
      ) : (
        <>
          <DataTableShell
            headerRow={
              <tr>
                <th className="text-center" style={{ width: 50 }}>{st('stt_column')}</th>
                <th>{st('ky_hieu_label')}</th>
                <th>{st('tu_so_column')}</th>
                <th>{st('den_so_column')}</th>
                <th className="text-end">{st('sl_column')}</th>
                <th className="text-end">{st('gia_tri_column')}</th>
                <th>{st('kho_column')}</th>
                <th>{st('trang_thai_column')}</th>
                <th>{st('ngay_phat_sinh_column')}</th>
              </tr>
            }
          >
            {rows.length === 0 ? (
              <tr><td colSpan={9} className="text-center text-muted py-5">{st('khong_co_du_lieu_text')}</td></tr>
            ) : (
              rows.map((row, index) => (
                <tr key={`${row.symbol}-${row.rangeStart || 'kho'}-${index}`}>
                  <td className="text-center">{getSttNumber(pagination.currentPage, pagination.pageSize, index)}</td>
                  <td>{row.symbol}</td>
                  <td>{row.rangeStart || '-'}</td>
                  <td>{row.rangeEnd || '-'}</td>
                  <td className="text-end">{formatQty(row.quantity)}</td>
                  <td className="text-end">{formatCurrency(row.value)}</td>
                  <td>{row.warehouseLabel}</td>
                  <td><span className={`badge bg-${STATUS_BADGE[row.status] || 'secondary'}-subtle text-${STATUS_BADGE[row.status] || 'secondary'}`}>{row.statusName}</span></td>
                  <td>{formatDateTime(row.issuedDate)}</td>
                </tr>
              ))
            )}
          </DataTableShell>

          <AgentPagination
            currentPage={pagination.currentPage}
            totalPages={pagination.totalPages}
            totalItems={pagination.totalItems}
            pageSize={pagination.pageSize}
            onPageChange={(page) => setPagination((prev) => ({ ...prev, currentPage: page }))}
          />
        </>
      )}
    </>
  );
};

export default IssuanceRangeTab;
